import { useMemo } from "react";
import { motion } from "framer-motion";
import { COPY } from "@/config/copy";
import { buildAuditParams } from "./results/auditParams";
import EditorialSection from "./results/EditorialSection";
import ResultsHero from "./results/ResultsHero";
import PrescriptionBlock from "./results/PrescriptionBlock";
import EmailRestartFooter from "./results/EmailRestartFooter";

// Stable content-hash key generator so we don't rely on array indices for React keys.
const contentKey = (prefix, text) => {
  const s = String(text || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return `${prefix}-${h}`;
};

export default function Results({ result, lead, onRestart, readonly = false }) {
  const { diagnosis, critique, prescription, gaps, email_status } = result;
  const primary = gaps[diagnosis.primary_gap];
  const secondary = gaps[diagnosis.secondary_gap];
  const offer = prescription?.offer;

  const r = COPY.results;
  const auditParams = useMemo(() => buildAuditParams({ result, lead }), [result, lead]);

  return (
    <div className="relative z-10 min-h-screen">
      <div className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#E5E5E5]">
        <div className="section-label">{r.header_left}</div>
        <div className="section-label">
          {r.header_right_prefix} {lead.business_name}
        </div>
      </div>

      <ResultsHero eyebrow={r.hero_eyebrow} primary={primary} />

      <EditorialSection
        label={r.sections.diagnosis.label}
        title={`${r.sections.diagnosis.title_prefix} ${primary.name}`}
        testid="section-diagnosis"
      >
        <p className="text-[17px] md:text-[18px] text-[#111] leading-[1.7] mb-6 font-sans" data-testid="editors-note">
          {critique.editors_note}
        </p>
        <p className="text-[15px] text-[#555] leading-[1.75] font-sans">{primary.description}</p>
      </EditorialSection>      <EditorialSection label={r.sections.cost.label} title={r.sections.cost.title} testid="section-cost">
        <p className="text-[17px] md:text-[18px] text-[#111] leading-[1.7] font-sans">
          {critique.cost}
        </p>
      </EditorialSection>

      <EditorialSection
        label={r.sections.symptoms.label}
        title={r.sections.symptoms.title}
        testid="section-symptoms"
      >
        {critique.what_we_noticed && (
          <p className="text-[17px] md:text-[18px] text-[#111] leading-[1.7] mb-8 font-sans">{critique.what_we_noticed}</p>
        )}
        <ul className="space-y-5">
          {(critique.where_showing_up || []).map((s, i) => (
            <motion.li
              key={contentKey("sym", s)}
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="text-[16px] md:text-[17px] text-[#111] leading-[1.65] pl-5 border-l-2 border-[#9fa5a3] font-sans"
              data-testid={`symptom-${i}`}
            >
              {s}
            </motion.li>
          ))}
        </ul>
      </EditorialSection>

      <EditorialSection
        label={r.sections.fix_first.label}
        title={r.sections.fix_first.title}
        testid="section-fix-first"
      >
        <div className="space-y-10">
          {(critique.fix_first || []).map((rec, i) => (
            <motion.div
              key={contentKey("rec", rec.title)}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`rec-${i}`}
              className="grid grid-cols-[auto,1fr] gap-6 items-start"
            >
              <div className="font-serif italic text-2xl text-[#9fa5a3] leading-none pt-1">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div>
                <div className="font-serif text-xl md:text-2xl leading-tight font-light mb-2 display-tight">
                  {rec.title}
                </div>
                <div className="text-[15px] md:text-[16px] text-[#333] leading-[1.7] font-sans">{rec.detail}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </EditorialSection>

      <EditorialSection
        label={r.sections.secondary.label}
        title={secondary.name}
        testid="section-secondary"
      >
        <p className="font-serif text-lg md:text-xl italic text-[#555] leading-snug mb-4 display-tight">
          {secondary.short}
        </p>
        <p className="text-[16px] md:text-[17px] text-[#111] leading-[1.7] font-sans">
          {critique.secondary_explanation || secondary.description}
        </p>
      </EditorialSection>      <PrescriptionBlock
        primary={primary}
        offer={offer}
        critique={critique}
        auditParams={auditParams}
        readonly={readonly}
        assessmentId={result.id}
      />

      {!readonly && (
        <EmailRestartFooter
          emailStatus={email_status}
          leadEmail={lead.email}
          onRestart={onRestart}
        />
      )}
    </div>
  );
}
